import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export interface DbTarget {
  id: string
  service_id: string | null
  service_name: string | null
  engine: string
  schedule_cron: string
  retention: Record<string, number>
  destination_path: string
  source_path: string | null
  encrypt_enabled: boolean
  enabled: boolean
  dsn_set: boolean
  last_run_at: string | null
  next_run_at: string | null
}

export interface BackupJob {
  id: string
  db_target_id: string
  status: 'queued' | 'running' | 'ok' | 'failed'
  started_at: string | null
  finished_at: string | null
  size_bytes: number | null
  duration_s: number | null
  sha256: string | null
  path: string | null
  error_text: string | null
  kind: string
}

export function useBackupTargets() {
  return useQuery<DbTarget[]>({
    queryKey: ['backup-targets'],
    queryFn: () => api<DbTarget[]>('/api/backups/targets'),
    refetchInterval: 30_000,
  })
}

export function useBackupJobs(targetId: string | undefined) {
  return useQuery<BackupJob[]>({
    queryKey: ['backup-jobs', targetId],
    queryFn: () => api<BackupJob[]>(`/api/backups/targets/${targetId}/jobs`),
    enabled: !!targetId,
    refetchInterval: 15_000,
  })
}

export function useRunBackup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api<BackupJob>(`/api/backups/targets/${id}/run-now`, {
        method: 'POST', body: JSON.stringify({}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['backup-jobs'] })
      qc.invalidateQueries({ queryKey: ['backup-targets'] })
    },
  })
}

export interface UpdateTargetBody {
  schedule_cron?: string
  retention?: Record<string, number>
  destination_path?: string
  source_path?: string
  encrypt_enabled?: boolean
  enabled?: boolean
  dsn_plain?: string
  s3_config?: Record<string, unknown>
}

export function useUpdateTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTargetBody }) =>
      api(`/api/backups/targets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup-targets'] }),
  })
}

export interface CreateTargetBody {
  engine: string
  source_path?: string
  service_name?: string
  schedule_cron?: string
}

export function useCreateTarget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateTargetBody) =>
      api<DbTarget>('/api/backups/targets', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backup-targets'] }),
  })
}
